"use client"

import * as React from "react"
import { useRouter } from "next/navigation"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

export function RegisterForm({ className, ...props }: React.ComponentPropsWithoutRef<"div">) {
    const router = useRouter()
    const [isLoading, setIsLoading] = React.useState(false)
    const [error, setError] = React.useState<string | null>(null)
    const [email, setEmail] = React.useState("")
    const [emailStatus, setEmailStatus] = React.useState<{
        message: string;
        available: boolean;
        status: "available" | "taken" | "limit_reached" | "pending" | "invalid" | "exists_but_available" | "loading" | null;
    }>({ message: "", available: true, status: null })

    // Email check effect with debounce
    React.useEffect(() => {
        if (!email) {
            setEmailStatus({ message: "", available: true, status: null })
            return
        }

        const timer = setTimeout(async () => {
            // Basic email validation before calling API
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            if (!emailRegex.test(email)) {
                setEmailStatus({ message: "Invalid email format", available: false, status: "invalid" })
                return
            }

            setEmailStatus(prev => ({ ...prev, status: "loading" }))

            try {
                const response = await fetch(`/api/auth/check-email?email=${encodeURIComponent(email)}`)
                const data = await response.json()

                if (response.ok) {
                    setEmailStatus({
                        message: data.message,
                        available: data.available,
                        status: data.status
                    })
                }
            } catch (err) {
                console.error("Email check failed:", err)
            }
        }, 500) // 500ms debounce

        return () => clearTimeout(timer)
    }, [email])

    async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault()
        setIsLoading(true)
        setError(null)

        const formData = new FormData(event.currentTarget)
        const emailValue = formData.get("email") as string
        const password = formData.get("password") as string
        const confirmPassword = formData.get("confirmPassword") as string
        const nickname = formData.get("nickname") as string

        if (password !== confirmPassword) {
            setError("Passwords do not match")
            setIsLoading(false)
            return
        }

        if (password.length < 6) {
            setError("Password must be at least 6 characters")
            setIsLoading(false)
            return
        }

        // If email isn't available, don't proceed
        if (emailStatus.status === "limit_reached" || emailStatus.status === "pending" || emailStatus.status === "invalid") {
            setError(emailStatus.message)
            setIsLoading(false)
            return
        }

        try {
            const response = await fetch("/api/register", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: emailValue, password, nickname }),
            })

            const data = await response.json()

            if (!response.ok) {
                throw new Error(data.error || "Registration failed")
            }

            // Redirect to success page with the login info
            const login = data.login || ''
            router.push(`/register/success?email=${encodeURIComponent(emailValue)}&login=${encodeURIComponent(login)}`)
        } catch (err) {
            if (err instanceof Error) {
                setError(err.message)
            } else {
                setError("Something went wrong. Please try again.")
            }
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div className={cn("grid gap-6", className)} {...props}>
            <form onSubmit={onSubmit}>
                <div className="grid gap-4">
                    <div className="grid gap-2">
                        <label className="text-sm font-medium leading-none" htmlFor="email">
                            Email
                        </label>
                        <input
                            id="email"
                            name="email"
                            placeholder="name@example.com"
                            type="email"
                            autoCapitalize="none"
                            autoComplete="email"
                            autoCorrect="off"
                            className={cn(
                                "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
                                emailStatus.status === "available" || emailStatus.status === "exists_but_available" ? "border-green-500/50 focus-visible:ring-green-500" : 
                                emailStatus.status && emailStatus.status !== "loading" ? "border-red-500/50 focus-visible:ring-red-500" : ""
                            )}
                            required
                            disabled={isLoading}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                        {emailStatus.status && (
                            <div className={cn(
                                "text-[10px] sm:text-xs font-medium",
                                emailStatus.available ? "text-green-500" : "text-red-500",
                                emailStatus.status === "loading" && "text-muted-foreground animate-pulse"
                            )}>
                                {emailStatus.status === "loading" ? "Checking availability..." : emailStatus.message}
                            </div>
                        )}
                    </div>
                    <div className="grid gap-2">
                        <label className="text-sm font-medium leading-none" htmlFor="nickname">
                            Nickname <span className="text-muted-foreground text-xs">(optional)</span>
                        </label>
                        <input
                            id="nickname"
                            name="nickname"
                            placeholder="Your display name"
                            type="text"
                            maxLength={100}
                            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                            disabled={isLoading}
                        />
                    </div>
                    <div className="grid gap-2">
                        <label className="text-sm font-medium leading-none" htmlFor="password">
                            Password
                        </label>
                        <input
                            id="password"
                            name="password"
                            type="password"
                            autoComplete="new-password"
                            minLength={6}
                            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                            required
                            disabled={isLoading}
                        />
                    </div>
                    <div className="grid gap-2">
                        <label className="text-sm font-medium leading-none" htmlFor="confirmPassword">
                            Confirm Password
                        </label>
                        <input
                            id="confirmPassword"
                            name="confirmPassword"
                            type="password"
                            autoComplete="new-password"
                            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                            required
                            disabled={isLoading}
                        />
                    </div>

                    {error && (
                        <div className="rounded-md bg-red-500/10 border border-red-500/20 p-3 text-sm text-red-500 font-medium">
                            {error}
                        </div>
                    )}

                    <Button 
                        disabled={isLoading || (emailStatus.status !== null && !emailStatus.available && emailStatus.status !== "loading")} 
                        className="w-full"
                    >
                        {isLoading && (
                            <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        )}
                        Create Account
                    </Button>
                </div>
            </form>

            <p className="text-center text-sm text-muted-foreground">
                Already have an account?{" "}
                <a href="/login" className="underline underline-offset-4 hover:text-primary">
                    Sign in
                </a>
            </p>
        </div>
    )
}
